import { StandardEvents } from '@shopify/events';
import { ringLabel } from '@theme/r69-ring-sizes';

/**
 * ROTAS69 size-remote — drives the main PDP ring-size option from a compact remote
 * <select> rendered in the promise "shop the piece" card and the sticky add-to-cart bar
 * (snippets/r69-size-remote.liquid). Desktop shows a custom "skin" over the native <select>
 * (like the complementary selector); mobile shows the native <select>.
 *
 * Two-way sync: a remote change drives the main `.variant-option--ringsize` control (select OR
 * radio, buttons mode) exactly like snippets/r69-ring-size-select.liquid; the main picker's
 * `shopify:product:select` re-syncs every remote. Morph-resilient: the sticky bar re-renders its
 * children on variant change (which strips the JS-built skin), so a MutationObserver rebuilds it.
 */
(function () {
  if (window.__r69SizeRemote) return;
  window.__r69SizeRemote = true;

  /* ---- main ring-size control adapter (buttons mode = radios, dropdowns = <select>) ---- */
  function mainCtl() {
    var host = document.querySelector('.variant-option--ringsize');
    if (!host) return null;
    var select = host.querySelector('select');
    if (select) {
      return {
        get: function () { return select.value; },
        set: function (v) {
          if (select.value === v) return false;
          select.value = v;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        },
      };
    }
    var radios = Array.prototype.slice.call(host.querySelectorAll('input[type="radio"]'));
    if (!radios.length) return null;
    return {
      get: function () {
        var r = radios.filter(function (x) { return x.checked; })[0];
        return r ? r.value : ((radios[0] && radios[0].value) || '');
      },
      set: function (v) {
        var r = radios.filter(function (x) { return x.value === v; })[0];
        if (!r || r.checked) return false;
        r.checked = true;
        r.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      },
    };
  }

  function labelFor(el, value) {
    var sname = el.dataset.sname || '';
    return (sname ? sname + ': ' : '') + ringLabel(value); // «Izmērs: Ø 17.3 mm»
  }

  /* ---- desktop skin over the native <select> ---- */
  // normalize the server-rendered <option> labels to a clean "Izmērs: Ø 17.4 mm"
  // (the Liquid compact render can leave stray whitespace)
  function normalizeLabels(el) {
    var sel = el.querySelector('select.r69size__native');
    if (!sel) return;
    Array.prototype.forEach.call(sel.options, function (o) {
      var clean = labelFor(el, o.value);
      if (o.textContent !== clean) o.textContent = clean;
    });
  }

  function buildSkin(el) {
    var sel = el.querySelector('select.r69size__native');
    if (!sel) return;
    normalizeLabels(el);
    var existing = el.querySelector('.r69size__skin');
    if (existing) existing.remove();

    var skin = document.createElement('div');
    skin.className = 'r69size__skin';

    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'r69size__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    var cur = document.createElement('span');
    cur.className = 'r69size__cur';
    cur.textContent = labelFor(el, sel.value);
    var caret = document.createElement('span');
    caret.className = 'r69size__caret';
    caret.setAttribute('aria-hidden', 'true');
    trigger.appendChild(cur);
    trigger.appendChild(caret);

    var panel = document.createElement('div');
    panel.className = 'r69size__panel';
    panel.hidden = true;
    panel.setAttribute('role', 'listbox');

    Array.prototype.forEach.call(sel.options, function (opt) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'r69size__opt';
      b.setAttribute('role', 'option');
      b.dataset.value = opt.value;
      b.textContent = opt.textContent;
      if (opt.disabled) b.classList.add('is-soldout');
      if (opt.value === sel.value) b.classList.add('is-selected');
      b.addEventListener('click', function (e) {
        e.preventDefault();
        if (b.classList.contains('is-soldout')) return;
        drive(el, opt.value);
        close();
      });
      panel.appendChild(b);
    });

    skin.appendChild(trigger);
    skin.appendChild(panel);
    el.appendChild(skin);

    // Width-sync (owner request): closed trigger as wide as the open list, nothing ellipsized.
    // Measure a clone parked on document.body — the hosting section may be content-visibility-
    // skipped at build time, where in-place geometry reads return 0. CSS max-width:100% clamps.
    // Measure PURE content width (widest option) on a body-parked clone — the section may be
    // content-visibility-skipped at build (in-place reads → 0). max-height:none + overflow:visible
    // so no scrollbar distorts max-content; reserve 16px for the 8px scrollbar + margin. PLAIN px,
    // NOT min(…,100%): the trigger is inline-flex with no definite containing block, so % collapses
    // to the natural width and min() truncates every option. max-width:100% is the overflow guard.
    var wProbe = panel.cloneNode(true);
    wProbe.hidden = false;
    wProbe.style.cssText = 'position:absolute;left:-9999px;top:0;display:block;visibility:hidden;width:max-content;max-height:none;overflow:visible;';
    document.body.appendChild(wProbe);
    var wContent = Math.ceil(wProbe.getBoundingClientRect().width);
    wProbe.parentNode.removeChild(wProbe);
    if (wContent > 0) trigger.style.minWidth = (wContent + 16) + 'px';

    function open() {
      if (!panel.hidden) return;
      panel.hidden = false;
      // Read ALL layout up front (batched) BEFORE any write, so the offsetWidth read no longer
      // forces a SECOND reflow after the classList writes below (open() was thrashing: read ->
      // write -> read). Now it's one layout pass per open.
      var trect = trigger.getBoundingClientRect();
      var ph = panel.offsetHeight;
      var below = window.innerHeight - trect.bottom;
      var above = trect.top;
      // open downward when there's room below; upward only when needed (sticky bar always up)
      el.classList.toggle('is-up', el.classList.contains('r69size--sticky') || (below < ph + 12 && above > below));
      trigger.setAttribute('aria-expanded', 'true');
      el.classList.add('r69size-open');
      // no open-time widening: the scrollbar width is baked into the trigger min-width at build,
      // so width:100% already fits options + scrollbar (open == closed, chevron gap constant)
      document.addEventListener('click', onOutside, true);
    }
    function close() {
      if (panel.hidden) return;
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      el.classList.remove('r69size-open');
      document.removeEventListener('click', onOutside, true);
    }
    function onOutside(e) { if (!el.contains(e.target)) close(); }
    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      if (panel.hidden) open(); else close();
    });
  }

  function updateSkin(el) {
    var sel = el.querySelector('select.r69size__native');
    var skin = el.querySelector('.r69size__skin');
    if (!sel || !skin) return;
    var cur = skin.querySelector('.r69size__cur');
    var lbl = labelFor(el, sel.value);
    // only WRITE when changed — a no-op textContent write is still a DOM mutation, which would
    // re-trigger the sticky-bar MutationObserver and loop forever
    if (cur && cur.textContent !== lbl) cur.textContent = lbl;
    Array.prototype.forEach.call(skin.querySelectorAll('.r69size__opt'), function (b) {
      b.classList.toggle('is-selected', b.dataset.value === sel.value);
    });
  }

  /* remote change -> reflect locally + drive the main control (fires productSelect -> resyncAll) */
  function drive(el, value) {
    var sel = el.querySelector('select.r69size__native');
    if (sel && sel.value !== value) sel.value = value;
    updateSkin(el);
    var ctl = mainCtl();
    if (ctl) ctl.set(value);
  }

  function resyncAll() {
    var ctl = mainCtl();
    if (!ctl) return;
    var v = ctl.get();
    Array.prototype.forEach.call(document.querySelectorAll('r69-size-remote'), function (el) {
      var sel = el.querySelector('select.r69size__native');
      if (!sel) return;
      if (v && sel.value !== v) sel.value = v; // programmatic set does NOT fire change -> no loop
      updateSkin(el);
    });
  }

  function wire(el) {
    if (!el.__wired) {
      el.__wired = true;
      // delegated so it survives the sticky bar's morph replacing the inner <select>
      el.addEventListener('change', function (e) {
        var t = e.target;
        if (t && t.classList && t.classList.contains('r69size__native')) drive(el, t.value);
      });
    }
    if (!el.querySelector('.r69size__skin')) buildSkin(el);
  }

  // Build each remote's skin when it becomes visible (IntersectionObserver), not on a global idle
  // timer — a page reloaded scrolled down slides the sticky bar in and the IO fires at once, so the
  // dropdown is there immediately (was blank for up to the idle timeout). Off-screen remotes (bundle
  // cards) still build lazily.
  var skinIO = null;
  function run() {
    if (!skinIO) {
      skinIO = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              wire(e.target);
              skinIO.unobserve(e.target);
            }
          });
        },
        { rootMargin: '300px' }
      );
    }
    Array.prototype.forEach.call(document.querySelectorAll('r69-size-remote'), function (el) {
      skinIO.observe(el);
    });
  }

  document.addEventListener(StandardEvents.productSelect, function () {
    resyncAll();
    [120, 350, 700].forEach(function (t) { setTimeout(resyncAll, t); });
  });

  // The sticky bar morphs its children on variant change (strips the JS-built skin); rebuild.
  // Disconnect while we mutate the skin ourselves so our own writes don't re-trigger the observer.
  function processRemotes() {
    Array.prototype.forEach.call(document.querySelectorAll('r69-size-remote'), function (el) {
      if (!el.__wired || !el.querySelector('.r69size__skin')) wire(el);
      else updateSkin(el);
    });
  }
  var mo = new MutationObserver(function () {
    mo.disconnect();
    processRemotes();
    observeSticky();
  });
  function observeSticky() {
    var bar = document.querySelector('.sticky-add-to-cart__bar');
    if (bar) mo.observe(bar, { childList: true, subtree: true });
  }

  // Boot ASAP — wiring up the observers is cheap; the skin build itself is visibility-gated by the
  // IntersectionObserver in run() (was idle-deferred, which left the sticky dropdown blank for up to
  // the idle timeout on a reload-scrolled-down).
  var booted = false;
  function boot() { if (booted) return; booted = true; run(); observeSticky(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
